import React, { useCallback, useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import WebView, { WebViewNavigation } from '@/components/Webview';
import { ds } from '@/components/lib/design.system';
import { DepositErrorResponse, useDeposit } from '@/feature/deposit/hooks/use-deposit';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { DepositStackParamList } from '@/navigation/types';
import { common } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';
import { getWalletErrorCode } from '@/utils/get-wallet-error-code';

import { DEPOSIT_STATUS_UNIVERSAL_LINK_URL, PaymentMethodTypes } from '../const';
import { useDepositStatusStore } from '../hooks/use-deposit-status-store';
import { useRejectDeposit } from '../hooks/use-reject-deposit';
import { useThreedsDeposit } from '../hooks/use-threeds-deposit';
import {
    PAYMENT_METHOD_NAME,
    PayPalStatus,
    PaymentProviderHandler,
    Paysafe3dsStatus,
    getPaymentProviderHandlers,
} from '../utils';

const styles = StyleSheet.create({
    loaderContainer: { backgroundColor: ds.colorValue.background.neutral.base },
});

type ScreenProps = NativeStackScreenProps<DepositStackParamList, 'DepositMethodWebViewModal'>;

const handleMatch = (match: RegExpMatchArray | null, handler: PaymentProviderHandler) => {
    if (match && match[1]) {
        const currentStatus = match[1];
        if (handler.statuses.includes(currentStatus)) {
            handler.onSuccess(currentStatus);
        } else if (currentStatus === PayPalStatus.DEFAULT) {
            handler.onDefault();
        }
    }
};

export const DepositMethodWebView = ({ route }: ScreenProps) => {
    const {
        selectedPaymentMethodType,
        currency = 'USD',
        payment_token = '',
        payment_type_id = '',
    } = route.params || {};
    const navigation = useNavigation<NativeStackNavigationProp<DepositStackParamList>>();
    const tSocialWallet = useSocialWalletTranslation('transactions');

    const selectedAmount = useDepositStatusStore(state => state.selectedAmount);
    const coinPackageId = useDepositStatusStore(state => state.coinPackageId);

    usePreventAndroidBackNavigation();

    const paymentProviderHandlers = getPaymentProviderHandlers(navigation, selectedPaymentMethodType);

    const handler = paymentProviderHandlers[selectedPaymentMethodType.split('_')[0]];

    const threedsMutation = useThreedsDeposit();
    const depositMutation = useDeposit();

    const isPaysafeCard =
        selectedPaymentMethodType === PaymentMethodTypes.Paysafe3ds ||
        selectedPaymentMethodType === PaymentMethodTypes.Paysafe;

    const mutation = isPaysafeCard ? threedsMutation : depositMutation;
    const { mutate: mutateDeposit, data: depositResponse, isPending } = mutation;

    const userWalletId = depositResponse?.extra.user_wallet_id;
    const { mutate: rejectDepositMutation } = useRejectDeposit();

    const webviewRef = useRef<WebView>(null);
    const handleNavigationChange = useCallback(
        () => (navState: WebViewNavigation) => {
            if (navState.url.startsWith(DEPOSIT_STATUS_UNIVERSAL_LINK_URL)) {
                const url = new URL(navState.url);
                const params = new URLSearchParams(url.search);

                const paymentProvider = params.get('paymentProvider') || '';
                const currentStatus = params.get('currentStatus') || '';

                if (currentStatus === PayPalStatus.SUCCESS || currentStatus === Paysafe3dsStatus.SUCCESS) {
                    navigation.navigate('SuccessfulDepositModal', {
                        paymentProvider: paymentProvider,
                    });
                } else {
                    navigation.navigate('FailedDepositModal', {
                        paymentProvider: paymentProvider,
                    });
                }
                return;
            }

            if (handler) {
                const match = navState?.url.match(handler.regex);
                handleMatch(match, handler);
            }
        },
        [handler, navigation]
    );
    useEffect(() => {
        mutateDeposit(
            {
                type: selectedPaymentMethodType,
                currency,
                payment_token,
                payment_type_id,
            },
            {
                onSuccess: data => {
                    // If payment_url is given, the user will be redirected automatically on the webview.
                    // If !payment_url that means we do not need to trigger 3ds webview, and deposit will be processed as usual
                    if (!data.payment_url) {
                        navigation.navigate('SuccessfulDepositModal', {
                            paymentProvider: selectedPaymentMethodType,
                            isRestrictedBin: data.is_restricted_bin || false,
                        });
                    }
                },
                onError: (error: DepositErrorResponse) => {
                    navigation.navigate('FailedDepositModal', {
                        paymentProvider: selectedPaymentMethodType,
                        errorCode: getWalletErrorCode(error),
                    });
                },
            }
        );
    }, [
        currency,
        mutateDeposit,
        navigation,
        payment_token,
        payment_type_id,
        selectedAmount,
        selectedPaymentMethodType,
        coinPackageId,
    ]);

    const paymentMethodTypeName = PAYMENT_METHOD_NAME[selectedPaymentMethodType];

    if (isPending) {
        return (
            <View style={[common.flex, styles.loaderContainer, common.justifyCenter]}>
                <LoadingSpinner />
            </View>
        );
    }

    if (!depositResponse) {
        return null;
    }
    if (isWeb && depositResponse.payment_url) {
        // if it's third party payment method, it is handled by the payment provider at their site.
        window.location.href = depositResponse.payment_url;
        return null;
    }

    return (
        <Screen>
            <ScreenNavBar
                title={tSocialWallet('deposit_with_payment_name', { paymentMethodTypeName })}
                closeIconMode="back"
                onClose={() => {
                    if (userWalletId) {
                        rejectDepositMutation(userWalletId);
                    }
                    navigation.goBack();
                }}
            />
            <WebView
                ref={webviewRef}
                source={{
                    uri: depositResponse.payment_url || '',
                }}
                onLoadStart={navState => {
                    if (handler) {
                        const match = navState.nativeEvent.url.match(handler.regex);
                        handleMatch(match, handler);
                    }
                }}
                onShouldStartLoadWithRequest={req => {
                    if (req.url.includes('paypal_default') && userWalletId) {
                        rejectDepositMutation(userWalletId);
                    }
                    return true;
                }}
                onNavigationStateChange={handleNavigationChange}
            />
        </Screen>
    );
};
